import { z } from 'zod';
import { tipoDeCierreSchema } from '../eventos.js';
/**
 * Los cierres del programa (CIERRES-1, CLAUDE.md §4.5 y
 * `escenarios-excepciones.html` sección I).
 *
 * Seis tipos: la finalización a los 365 días (más la extensión) y los cinco no
 * programados. **Un cierre no se deshace y un programa se cierra una sola vez**:
 * lo sostienen la base (un trigger) y el servicio (una escritura condicionada a
 * que el programa siga activo). Lo que sí admite el abandono es un reingreso,
 * que es un programa nuevo desde el día 1 (§4.4: el modelo multi-episodio
 * formal está diferido).
 *
 * Lo que se registra de cada tipo es exactamente lo que dice la sección I. Sin
 * campos de más: el fallecimiento no pide la fecha del fallecimiento, pide
 * cuándo y por dónde se supo.
 */
/** Los tipos que cierra una persona del equipo desde el formulario M-13. */
export const TIPOS_DE_CIERRE = tipoDeCierreSchema.options;
/**
 * Quién puede ejecutar cada tipo.
 *
 * - **Fallecimiento**, sólo el profesional (el "profesional autorizado" de la
 *   sección I ①).
 * - **Pérdida de seguimiento**, sólo el coordinador (sección I ③: "lo ejecuta el
 *   coordinador tras confirmar que se agotaron las vías de contacto").
 * - **Alta médica anticipada**, sólo el profesional: es una decisión clínica con
 *   su justificación (sección I ④; decisión de Cowork del 3/11, pregunta 2).
 * - **Finalización**, el sistema solo a los 365 días o al terminar la extensión
 *   (decisión de Cowork del 3/11, pregunta 1); el formulario la ofrece igual a
 *   las dos personas del equipo para un programa que ya terminó y el job
 *   todavía no cerró.
 * - El resto, el profesional y el coordinador.
 *
 * Ni el paciente, ni el cuidador, ni las administraciones cierran un programa.
 */
export const ROLES_QUE_CIERRAN = Object.freeze({
    closed_normal: ['PROFESIONAL', 'COORDINADOR'],
    closed_deceased: ['PROFESIONAL'],
    closed_voluntary: ['PROFESIONAL', 'COORDINADOR'],
    closed_lost: ['COORDINADOR'],
    closed_early_discharge: ['PROFESIONAL'],
    closed_referred: ['PROFESIONAL', 'COORDINADOR'],
});
export function puedeCerrar(rol, tipo) {
    return ROLES_QUE_CIERRAN[tipo].includes(rol);
}
/** Por dónde se supo un fallecimiento (sección I ①, canal a). */
export const canalDeConocimientoSchema = z.enum([
    'en_la_institucion',
    'familia_o_cuidador',
    'otra_institucion',
    'otro_canal',
]);
/** Texto del profesional: frases, no párrafos. Sin controles ni saltos raros. */
const textoDelEquipo = (maximo) => z
    .string()
    .trim()
    .min(3)
    .max(maximo)
    // biome-ignore lint/suspicious/noControlCharactersInRegex: se rechazan a propósito.
    .refine((texto) => !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(texto), {
    message: 'caracteres_invalidos',
});
export const MAXIMO_DEL_MOTIVO = 1000;
export const MAXIMO_DE_LA_INSTITUCION = 160;
/** La solicitud del formulario M-13: un tipo y sus campos, y nada más. */
export const solicitudDeCierreSchema = z.discriminatedUnion('tipo', [
    z.strictObject({ tipo: z.literal('closed_normal') }),
    z.strictObject({
        tipo: z.literal('closed_deceased'),
        conocidoEl: z.iso.date(),
        canal: canalDeConocimientoSchema,
    }),
    z.strictObject({
        tipo: z.literal('closed_voluntary'),
        motivoDeclarado: textoDelEquipo(MAXIMO_DEL_MOTIVO),
    }),
    // La pérdida de seguimiento no pide nada: el último contacto y los intentos
    // los precarga el servidor, y el resultado es siempre "desconocido".
    z.strictObject({ tipo: z.literal('closed_lost') }),
    z.strictObject({
        tipo: z.literal('closed_early_discharge'),
        justificacion: textoDelEquipo(MAXIMO_DEL_MOTIVO),
    }),
    z.strictObject({
        tipo: z.literal('closed_referred'),
        institucionDestino: textoDelEquipo(MAXIMO_DE_LA_INSTITUCION),
        motivo: textoDelEquipo(MAXIMO_DEL_MOTIVO),
        continuidadDigital: z.boolean(),
    }),
]);
// ── Pérdida de seguimiento ─────────────────────────────────────────────────
/** Días sin ninguna interacción que pide la sección I ③. */
export const DIAS_SIN_INTERACCION_PARA_PERDIDA = 30;
export const condicionDePerdidaSchema = z.enum([
    'silencio_agotado',
    'cuidador_sin_respuesta',
    'treinta_dias_sin_interaccion',
]);
/**
 * Función pura: las condiciones de la pérdida de seguimiento que todavía no se
 * cumplen. Vacía, el coordinador puede cerrar.
 */
export function condicionesQueFaltan(hechos) {
    const faltan = [];
    if (!hechos.silencioAgotado)
        faltan.push('silencio_agotado');
    // "Contacto con cuidador fallido o inexistente": sin cuidador, la condición
    // se cumple; con cuidador, se cumple si no respondió.
    if (hechos.hayCuidador && hechos.cuidadorRespondio)
        faltan.push('cuidador_sin_respuesta');
    if (hechos.diasSinInteraccion < DIAS_SIN_INTERACCION_PARA_PERDIDA) {
        faltan.push('treinta_dias_sin_interaccion');
    }
    return faltan;
}
export const intentoDelProtocoloSchema = z.strictObject({
    at: z.iso.datetime(),
    nivel: z.union([z.literal(1), z.literal(2)]),
    canal: z.enum(['pia', 'push', 'whatsapp']),
    resultado: z.enum(['enviado', 'fallido', 'sin_canal']),
});
// ── Lo que el formulario necesita saber antes de abrir ─────────────────────
/** Por qué un tipo no se puede elegir en este caso, si no se puede. */
export const bloqueoDeCierreSchema = z.enum(['rol', 'programa_no_termino', 'faltan_condiciones']);
export const opcionDeCierreSchema = z.strictObject({
    tipo: tipoDeCierreSchema,
    bloqueo: bloqueoDeCierreSchema.nullable(),
});
export const preparacionDelCierreSchema = z.strictObject({
    programaId: z.uuid(),
    /** Nulo si el programa está abierto. */
    cierre: z
        .strictObject({
        tipo: tipoDeCierreSchema,
        cerradoAt: z.iso.datetime(),
        cerradoPorAlias: z.string().nullable(),
    })
        .nullable(),
    opciones: z.array(opcionDeCierreSchema),
    perdida: z.strictObject({
        faltan: z.array(condicionDePerdidaSchema),
        ultimoContactoAt: z.iso.datetime().nullable(),
        intentos: z.array(intentoDelProtocoloSchema),
    }),
});
export const resultadoDelCierreSchema = z.strictObject({
    programaId: z.uuid(),
    tipo: tipoDeCierreSchema,
    cerradoAt: z.iso.datetime(),
});
// ── Lo que ve la persona al terminar ───────────────────────────────────────
/**
 * La pantalla "Tu programa terminó" del paciente, por tipo. En fallecimiento y
 * pérdida de seguimiento no hay pantalla: el acceso deja de funcionar.
 */
export const varianteDelFinSchema = z.enum(['positivo', 'abandono', 'derivacion']);
export function varianteDelFin(tipo) {
    switch (tipo) {
        case 'closed_normal':
        case 'closed_early_discharge':
            return 'positivo';
        case 'closed_voluntary':
            return 'abandono';
        case 'closed_referred':
            return 'derivacion';
        case 'closed_deceased':
        case 'closed_lost':
            return null;
    }
}
export const finDelPacienteSchema = z.strictObject({
    variante: varianteDelFinSchema,
    /** Sólo en la derivación: adónde sigue el seguimiento. */
    institucionDestino: z.string().nullable(),
    /**
     * El texto de Fundación para "Para seguir cuidándote", si ya llegó. Nulo y
     * la pantalla no muestra el bloque: el contenido de salud para el paciente
     * lo escribe Fundación, nunca el equipo de desarrollo (P-138).
     */
    paraSeguir: z.string().nullable(),
});
/**
 * El texto de cierre de la red para el paciente, por tipo · lo escribe
 * Fundación (`dev/pendientes-fundacion.md`, material que falta). **Vacío hasta
 * que llegue**: con `null`, la pantalla no muestra el bloque. Es configuración
 * de la red y no de cada institución, como las bandas del semáforo: se cambia
 * por PR cuando Fundación lo mande. No se redacta acá contenido de salud para
 * el paciente (P-138).
 */
export const TEXTO_DE_CIERRE_DE_LA_RED = Object.freeze({
    closed_normal: null,
    closed_early_discharge: null,
    closed_referred: null,
});
export function textoDeCierreDeLaRed(tipo) {
    if (tipo === 'closed_normal' || tipo === 'closed_early_discharge' || tipo === 'closed_referred') {
        return TEXTO_DE_CIERRE_DE_LA_RED[tipo];
    }
    return null;
}
/**
 * Si cerrar con este tipo corta el acceso de la persona en el mismo instante,
 * sin pantalla de despedida (sección I ① y ③).
 */
export function cortaElAccesoDelPaciente(tipo) {
    return tipo === 'closed_deceased' || tipo === 'closed_lost';
}
/**
 * Si el alta de la misma persona en la misma institución puede ser un
 * reingreso después de este cierre. Sólo el fallecimiento lo impide; el
 * reingreso es siempre un programa nuevo desde el día 1.
 */
export function admiteReingreso(tipo) {
    return tipo !== 'closed_deceased';
}
//# sourceMappingURL=cierre.js.map